/**
 * Public read-only Oracle conversation viewer at /oracle/c/:slug.
 * Anyone can open this link — no login required.
 */
import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { api } from "../lib/api";

function relativeTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" });
}

export default function OracleSharedConversation() {
  const { slug } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: payload } = await api.get(`/oracle/c/${slug}`);
        if (!cancelled) setData(payload);
      } catch (err) {
        if (!cancelled) setError(err?.response?.status === 404 ? "not_found" : "error");
      }
    })();
    return () => { cancelled = true; };
  }, [slug]);

  if (error === "not_found") {
    return (
      <div data-testid="oracle-public-missing" className="oracle-theme min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-20">
        <div className="text-center max-w-md">
          <div className="label mb-4">Not found</div>
          <h1 className="serif text-3xl mb-4">This conversation isn&apos;t public.</h1>
          <p className="text-[hsl(var(--oracle-foreground)/0.7)] mb-8">
            It may have been unshared or never existed at this address.
          </p>
          <Link to="/oracle" className="btn-oracle inline-flex items-center gap-2" data-testid="oracle-public-back">
            <ArrowLeft size={14} /> Ask the Oracle yourself
          </Link>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="oracle-theme min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-20">
        <p className="text-[hsl(var(--oracle-foreground)/0.7)] serif italic">Something went wrong loading this conversation.</p>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="oracle-theme min-h-[calc(100vh-80px)] flex items-center justify-center px-6 py-20">
        <p className="text-[hsl(var(--oracle-foreground)/0.6)] mono text-[10px] uppercase tracking-[0.2em]">Loading…</p>
      </div>
    );
  }

  const { title, shared_at, messages = [], author } = data;

  return (
    <div data-testid="oracle-public-page" className="oracle-theme min-h-[calc(100vh-80px)]">
      <div className="max-w-3xl mx-auto px-6 sm:px-10 py-20">
        <header className="mb-14">
          <Link to="/oracle" className="reaction-pill mb-8 inline-flex" data-testid="oracle-public-ask">
            <ArrowLeft size={12} /> Ask your own
          </Link>
          <div className="label mb-3">A shared conversation with the Oracle</div>
          <h1 className="serif text-4xl sm:text-5xl leading-tight">{title}</h1>
          <div className="mono text-[10px] uppercase tracking-[0.18em] text-[hsl(var(--oracle-foreground)/0.55)] mt-6 flex flex-wrap gap-x-4 gap-y-1">
            {author?.handle ? (
              <Link to={`/u/${author.handle}`} className="hover:text-[hsl(var(--oracle-accent))]" data-testid="oracle-public-author">
                Shared by @{author.handle}
              </Link>
            ) : author?.name ? (
              <span>Shared by {author.name}</span>
            ) : (
              <span>Shared anonymously</span>
            )}
            {shared_at && <span>· {relativeTime(shared_at)}</span>}
          </div>
        </header>

        <div className="space-y-10" data-testid="oracle-public-thread">
          {messages.map((m, i) => (
            // Stable key: role + position + first slice of content. The
            // conversation is immutable once loaded, so this holds across
            // renders even if two messages happen to share role/order.
            <div key={`${m.role}-${i}-${(m.content || '').slice(0, 24)}`} data-testid={`oracle-public-msg-${m.role}-${i}`}>
              <div className="label mb-3">{m.role === "user" ? "Question" : "The Oracle"}</div>
              <div className={`${m.role === "user" ? "serif text-2xl italic text-[hsl(var(--oracle-foreground))]" : "text-[hsl(var(--oracle-foreground)/0.92)] leading-[1.85] whitespace-pre-wrap"} max-w-3xl`}>
                {m.content}
              </div>
            </div>
          ))}
        </div>

        <footer className="hairline-t mt-16 pt-8 text-center">
          <p className="serif italic text-[hsl(var(--oracle-foreground)/0.6)] mb-6">
            Bring your own question to the archive.
          </p>
          <Link to="/oracle" className="btn-oracle inline-flex items-center gap-2" data-testid="oracle-public-cta">
            Ask the Oracle
          </Link>
        </footer>
      </div>
    </div>
  );
}
